import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { getOrganization, createOrganization, updateOrganization } from "../lib/api";
import { ArrowLeft, Upload, Building2, Save, Image as ImageIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const EMPTY = { name: "", address: "", email: "", phone: "", web: "", logo_base64: null, notes: "" };

export default function OrganizationForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (id) getOrganization(id).then(setForm).catch(() => toast.error("Kuruluş yüklenemedi"));
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) {
      toast.error("Logo 1.5 MB'dan küçük olmalı");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => set("logo_base64", reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Kuruluş adı zorunludur"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (id) await updateOrganization(id, payload);
      else await createOrganization(payload);
      toast.success(id ? "Kuruluş güncellendi" : "Kuruluş oluşturuldu");
      nav("/organizations");
    } catch (e) {
      toast.error("Kayıt başarısız");
    } finally { setSaving(false); }
  };

  return (
    <div className="px-6 md:px-14 py-10 md:py-14 max-w-3xl">
      <Link to="/organizations" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="size-4" /> Tesisler
      </Link>
      <h1 className="font-display text-4xl font-semibold mb-2">
        {id ? "Tesisi Düzenle" : "Yeni Tesis"}
      </h1>
      <p className="text-muted-foreground mb-10">Tesis bilgileri rapor oluşturmada kullanılacaktır.</p>

      <div className="space-y-6">
        {/* Logo */}
        <div className="p-6 rounded-2xl border border-border bg-card">
          <Label className="text-sm font-medium mb-3 block">Tesis Logosu</Label>
          <div className="flex items-center gap-5">
            <div className="size-24 rounded-2xl bg-secondary flex items-center justify-center overflow-hidden border border-border">
              {form.logo_base64 ? (
                <img src={form.logo_base64} alt="" className="object-contain w-full h-full" />
              ) : (
                <ImageIcon className="size-8 text-muted-foreground" strokeWidth={1.4} />
              )}
            </div>
            <div>
              <input ref={fileRef} type="file" accept="image/*" onChange={handleLogo} className="hidden" data-testid="logo-file-input" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} className="gap-2" data-testid="logo-upload-btn">
                <Upload className="size-4" /> Logo Yükle
              </Button>
              {form.logo_base64 && (
                <Button variant="ghost" size="sm" onClick={() => set("logo_base64", null)} className="ml-2 text-destructive">Kaldır</Button>
              )}
              <div className="text-xs text-muted-foreground mt-2">PNG, JPG · Max 1.5 MB</div>
            </div>
          </div>
        </div>

        {/* Ana bilgiler */}
        <div className="p-6 rounded-2xl border border-border bg-card space-y-5">
          <Field label="Tesis Adı *">
            <Input data-testid="org-name" value={form.name} onChange={(e) => set("name", e.target.value)}
                   placeholder="Örn: Şenyıldız Otel" />
          </Field>
          <Field label="Adres">
            <Textarea data-testid="org-address" value={form.address} onChange={(e) => set("address", e.target.value)}
                      rows={2} placeholder="Cadde, No, İlçe/İl" />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="E-posta">
              <Input data-testid="org-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                     placeholder="info@otel.com" />
            </Field>
            <Field label="Telefon">
              <Input data-testid="org-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)}
                     placeholder="0xxx xxx xx xx" />
            </Field>
          </div>
          <Field label="Web Sitesi">
            <Input data-testid="org-web" value={form.web} onChange={(e) => set("web", e.target.value)}
                   placeholder="https://..." />
          </Field>
          <Field label="Ek Notlar">
            <Textarea data-testid="org-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)}
                      rows={3} placeholder="Tesisle ilgili notlar..." />
          </Field>
        </div>

        <div className="flex gap-3 justify-end">
          <Link to="/organizations"><Button variant="outline" className="rounded-full px-6">İptal</Button></Link>
          <Button onClick={save} disabled={saving} className="rounded-full px-7 gap-2" data-testid="save-org-btn">
            <Save className="size-4" /> {saving ? "Kaydediliyor…" : "Kaydet"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-sm font-medium mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
